import { Inject, Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { UrlKey, IHttpService, WebLogger } from "@weps/shared-http";
import { sanitizeEstateProfileData } from "@models/estateProfile.model";
import { withJourney } from "@vanguard/otel-web-tools";
import { withOutboundRequest } from "@vanguard/otel-web-tools";
import { JOURNEYS } from "@services/otel/journeys";
import {
  RobGetResponse,
  RobMutateResponse,
  RobMutationPersonData,
  FinancialInstitution,
  RobInstitutionSearchResponse,
} from "@models/rob.model";
import * as queries from "../../services/query/query";
import { AccountType } from "../../types/AccountType";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import * as constants from "../../services/content/content";
import { scrollToTopAfterData } from "@weps/shared-ui";
//import mockdata from '../../mock-data/mock-estate-profile-trust-scenarios.json'  // Uncomment for trust testing

export enum MutationType {
  ACCOUNT = "account",
  ASSET = "asset",
  LIABILITY = "liability",
  TRUST = "trust",
}

@Injectable({
  providedIn: "root",
})
export class RobService {
  private readonly globalStore = inject(GlobalStateStore);
  private robDataLoaded = new Subject<void>();
  private robMutationCall = new Subject<RobMutateResponse>();

  constructor(@Inject("IHttpService") private httpService: IHttpService) {}

  // TODO: Tricky to make this generic since we're checking something specific inside the data object
  callRobMutation(): void {
    withOutboundRequest({ appPrefix: "ROB", route: "/graphql" }, () => {
      this.httpService.doPostRequest(UrlKey.rob_graphql_url, this.getMutationData()).subscribe({
        next: (res: RobMutateResponse) => {
          if (
            res.data &&
            res.data.compressedEstateProfileCreate &&
            res.data.compressedEstateProfileCreate.statusCode == 201
          ) {
            const estateProfileId = res.data.compressedEstateProfileCreate.estateProfileId;
            if (estateProfileId) {
              this.globalStore.updateEstateProfileId(estateProfileId);
            }
          }
          WebLogger.log({
            message: "ROB compressedEstateProfileCreate mutation succeeded",
            level: "info",
            payload: {
              event_type: "api_call_complete",
              service: "rob",
              api_name: "estate_profile",
              http_method: "POST",
              status_code: res?.data?.compressedEstateProfileCreate?.statusCode || 201,
              operation: "compressedEstateProfileCreate",
              estate_profile_id: res?.data?.compressedEstateProfileCreate?.estateProfileId,
            },
          });
          this.robMutationCall.next(res);
        },
        error: (err: unknown) => {
          WebLogger.log({
            message: "ROB compressedEstateProfileCreate mutation failed",
            level: "error",
            payload: {
              event_type: "api_call_failed",
              service: "rob",
              api_name: "estate_profile",
              http_method: "POST",
              operation: "compressedEstateProfileCreate",
              error_message: err instanceof Error ? err.message : String(err),
              error_type: err instanceof Error ? err.name : "UnknownError",
              status_code: 0,
            },
          });
          this.robMutationCall.error(err);
        },
      });
    });
  }

  getRobData(): void {
    // ============================================================================
    // TODO: This section can load ROB mock data instead of making actual calls to ROB
    // TODO: Can essentially be used when ROB's down or if you're testing locally but don't need to test actual calls (essentially just testing UI updates). Can also be helpful if Client Search is down and you still need to test the UI
    // To enable mock data for local testing, just uncomment the if block below:
    // if (environment.deployment === 'local') {
    //   console.log('[ROB Service] Running in local environment. Using mock data for estate profile.');
    //   const mockEstateProfile = mockData;
    //
    //   const sanitizedMockProfile = sanitizeEstateProfileData(mockEstateProfile);
    //   this.globalStore.updateEstateProfile(sanitizedMockProfile);
    //
    //   // Emit signal that data has been retrieved
    //   this.robDataLoaded.next();
    //   return;
    // }
    // ============================================================================

    withJourney({ journey: JOURNEYS.pageLoad }, (journeySpan) => {
      const query = { query: queries.GET_CLIENT_QUERY, generateEUPToken: true };
      withOutboundRequest({ appPrefix: "ROB", route: "/graphql" }, () => {
        this.httpService
          .doPostRequest<RobGetResponse>(UrlKey.rob_graphql_url, query)
          .pipe(scrollToTopAfterData())
          .subscribe({
            next: (res: RobGetResponse) => {
              const sanitizedProfile = sanitizeEstateProfileData(res.data.estateProfile);
              this.globalStore.updateEstateProfile(sanitizedProfile);

              // Emit signal that data has been retrieved
              this.robDataLoaded.next();
              journeySpan.getSpan().end();
            },
            error: (error: unknown) => {
              WebLogger.log({
                message: "ROB estate profile query failed (GET_CLIENT_QUERY)",
                level: "error",
                payload: {
                  event_type: "api_call_failed",
                  service: "rob",
                  api_name: "estate_profile",
                  http_method: "POST",
                  operation: "GET_CLIENT_QUERY",
                  error_message: error instanceof Error ? error.message : String(error),
                  error_type: error instanceof Error ? error.name : "UnknownError",
                  status_code: 0,
                },
              });
              journeySpan.setErrorState(error instanceof Error ? error : new Error(String(error)));
              journeySpan.getSpan().end();
              this.robDataLoaded.error(error);
            },
          });
      });
    });
  }

  getRobDataLoadedObservable(): Observable<any> {
    return this.robDataLoaded.asObservable();
  }

  getRobMutationObservable(): Observable<any> {
    return this.robMutationCall.asObservable();
  }

  getMutationData(): Object {
    const estateProfile = this.globalStore.estateProfile?.() ?? undefined;
    if (!estateProfile) {
      throw new Error("Estate profile is undefined. Cannot create mutation data.");
    }

    const primaryClient: RobMutationPersonData = queries.mapToRobMutationPersonData(
      estateProfile.primaryClient,
    );
    const people: RobMutationPersonData[] = [];

    const allSpouses = this.globalStore.findAllSpouseData();
    for (const spouse of allSpouses) {
      people.push(queries.mapToRobMutationPersonData(spouse));
    }

    const allAccounts = [
      ...estateProfile.accounts,
      ...estateProfile.manuallyEnteredOutsideAccounts,
    ];
    const allAssets = [...estateProfile.manuallyEnteredOutsideAssets];
    const allLiabilities = [...estateProfile.manuallyEnteredLiabilities];

    return {
      query: queries.getEstateProfileMutation(
        estateProfile.primaryClient?.lastName
          ? `${estateProfile.primaryClient.lastName} ${constants.FAMILY_WEALTH_PLAN}`
          : "",
        primaryClient,
        people,
        allAccounts.map(queries.mapToRobMutationAccountData),
        allAssets.map(queries.mapToRobMutationAssetData),
        allLiabilities.map(queries.mapToRobMutationLiabilityData),
      ),
    };
  }

  buildOwnerQueries(type: MutationType): Object {
    const state = this.globalStore.guidedFlowState();

    switch (type) {
      case MutationType.TRUST:
        return this.buildTrustMutationQuery(state);

      default:
        throw new Error("Invalid mutation type");
    }
  }

  buildMeoaMutationQuery(type: MutationType): Object {
    try {
      const state = this.globalStore.guidedFlowState();

      switch (type) {
        case MutationType.ACCOUNT:
          return this.buildAccountMutationQuery(state);

        case MutationType.ASSET:
          if (state[AccountType.REAL_ESTATE]?.isEdit) {
            return queries.editAssetAndLiabilityMutationQuery(state);
          } else {
            return this.buildAssetMutationQuery(state);
          }

        case MutationType.LIABILITY:
          return this.buildLiabilityMutationQuery(state);

        //TODO: leaving this here for now until VG trusts are decoupled completely from accounts
        case MutationType.TRUST:
          return this.buildTrustMutationQuery(state);

        default:
          throw new Error("Invalid mutation type");
      }
    } catch (error) {
      WebLogger.log({
        message: "ROB MEOA mutation query build error",
        level: "error",
        payload: {
          meoa_mutation_type: type,
          error:
            error instanceof Error ? { message: error.message, stack: error.stack } : String(error),
        },
      });
      throw error;
    }
  }

  private buildAccountMutationQuery(state): Object {
    return { query: queries.getAccountMutationQuery(state) };
  }

  private buildAssetMutationQuery(state): Object {
    try {
      const mortgageAmount = state[AccountType.REAL_ESTATE]?.mortgageAmount;

      // TODO: Future-state, you will just pass the state to getAssetAndLiabilityMutationQuery
      if (mortgageAmount) {
        return { query: queries.getAssetAndLiabilityMutationQuery(state) };
      }

      return { query: queries.getAssetMutationQuery(state) };
    } catch (error) {
      WebLogger.log({
        message: "ROB MEOA asset mutation query build error",
        level: "error",
        payload: {
          meoa_mutation_type: "ASSET",
          error:
            error instanceof Error ? { message: error.message, stack: error.stack } : String(error),
        },
      });
      throw error;
    }
  }

  private buildLiabilityMutationQuery(state): Object {
    return { query: queries.getLiabilityMutationQuery(state) };
  }

  private buildTrustMutationQuery(state): Object {
    try {
      return { query: queries.getTrustMutationQuery(state) };
    } catch (error) {
      WebLogger.log({
        message: "ROB MEOA trust mutation query build error",
        level: "error",
        payload: {
          meoa_mutation_type: "TRUST",
          error:
            error instanceof Error ? { message: error.message, stack: error.stack } : String(error),
        },
      });
      throw error;
    }
  }

  updateInventoryStatus(item: any): void {
    // Determine entity type and ID
    let entityType: string;
    let entityId: string;

    if ("accountId" in item) {
      entityType = "account";
      entityId = item.accountId;
    } else if ("assetId" in item) {
      entityType = "asset";
      entityId = item.assetId;
    } else if ("liabilityId" in item) {
      entityType = "liability";
      entityId = item.liabilityId;
    } else {
      WebLogger.log({
        message: "ROB updateInventoryStatus - unknown entity type",
        level: "error",
        payload: { item },
      });
      return;
    }

    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile?.estateProfileId) {
      WebLogger.log({
        message: "ROB updateInventoryStatus - missing estateProfileId",
        level: "error",
      });
      return;
    }

    WebLogger.log({
      message: "ROB updateInventoryStatus called",
      level: "info",
      payload: {
        entityType,
        entityId,
        excludeFromInventory: item.excludeFromInventory,
        estateProfileId: estateProfile.estateProfileId,
      },
    });

    // Build the mutation input
    const input = {
      estateProfileId: estateProfile.estateProfileId,
      entityType: entityType,
      entityId: entityId,
      excludeFromInventory: item.excludeFromInventory,
      inEstateProfile: item.inEstateProfile,
    };

    const formattedInput = JSON.stringify(input).replace(/"([^"]+)":/g, "$1:");
    const mutationQuery = queries.UPDATE_EXCLUDE_FROM_INVENTORY_MUTATION(formattedInput);
    const combinedMutation = { query: `mutation { ${mutationQuery} }` };

    WebLogger.log({
      message: "ROB update inventory mutation query built",
      level: "info",
      payload: { mutationQuery: mutationQuery },
    });

    // Execute the mutation
    this.httpService.doPostRequest(UrlKey.rob_graphql_url, combinedMutation).subscribe({
      next: (res: any) => {
        WebLogger.log({
          message: "ROB update inventory status succeeded",
          level: "info",
          payload: {
            event_type: "api_call_complete",
            service: "rob",
            api_name: "estate_profile",
            http_method: "POST",
            operation: "update_inventory_status",
            status_code: res?.data?.updateExcludeFromInventory?.statusCode || 200,
            entityType,
            entityId,
          },
        });
        this.robMutationCall.next(res);
      },
      error: (err: unknown) => {
        WebLogger.log({
          message: "ROB update inventory status failed",
          level: "error",
          payload: {
            event_type: "api_call_failed",
            service: "rob",
            api_name: "estate_profile",
            http_method: "POST",
            operation: "update_inventory_status",
            error_message: err instanceof Error ? err.message : String(err),
            error_type: err instanceof Error ? err.name : "UnknownError",
            status_code: 0,
            entityType,
            entityId,
          },
        });
        this.robMutationCall.error(err);
      },
    });
  }

  deleteInventoryItem(estateProfileId: string, entityType: string, entityId: string): void {
    WebLogger.log({
      message: "ROB deleteInventoryItem called",
      level: "info",
      payload: {
        entityType,
        entityId,
        estateProfileId,
      },
    });

    // Backend is now deployed - using real GraphQL mutation
    const useMocks = false;

    if (useMocks) {
      this.mockDeleteInventoryItem(estateProfileId, entityType, entityId);
      return;
    }

    // Real GraphQL mutation (will be implemented when backend is ready)
    const mutation = {
      query: `
        mutation DeleteInventoryItem($input: DeleteInventoryItemInput!) {
          deleteInventoryItem(input: $input) {
            statusCode
            message
          }
        }
      `,
      variables: {
        input: {
          estateProfileId,
          entityType: entityType.toUpperCase(),
          entityId,
        },
      },
    };

    this.httpService.doPostRequest(UrlKey.rob_graphql_url, mutation).subscribe({
      next: (res: any) => {
        WebLogger.log({
          message: `ROB deleteInventoryItem mutation succeeded for ${entityType}`,
          level: "info",
          payload: {
            event_type: "api_call_complete",
            service: "rob",
            api_name: "delete_inventory_item",
            http_method: "POST",
            status_code: res?.data?.deleteInventoryItem?.statusCode || 200,
            operation: "deleteInventoryItem",
            estate_profile_id: estateProfileId,
            entity_type: entityType,
            entity_id: entityId,
          },
        });
        this.robMutationCall.next(res);
      },
      error: (err: unknown) => {
        WebLogger.log({
          message: `ROB deleteInventoryItem mutation failed for ${entityType}`,
          level: "error",
          payload: {
            event_type: "api_call_failed",
            service: "rob",
            api_name: "delete_inventory_item",
            http_method: "POST",
            operation: "deleteInventoryItem",
            error_message: err instanceof Error ? err.message : String(err),
            error_type: err instanceof Error ? err.name : "UnknownError",
            status_code: 0,
            entity_type: entityType,
            entity_id: entityId,
          },
        });
        this.robMutationCall.error(err);
      },
    });
  }

  private mockDeleteInventoryItem(
    estateProfileId: string,
    entityType: string,
    entityId: string,
  ): void {
    WebLogger.log({
      message: `Mock deleteInventoryItem - simulating deletion of ${entityType}`,
      level: "info",
      payload: {
        estateProfileId,
        entityType,
        entityId,
        mock: true,
      },
    });

    // Simulate network delay (1.5 seconds)
    setTimeout(() => {
      const mockResponse = {
        data: {
          deleteInventoryItem: {
            statusCode: 200,
            message: `Successfully deleted ${entityType}`,
          },
        },
        errors: [],
      };

      WebLogger.log({
        message: `Mock deleteInventoryItem completed for ${entityType}`,
        level: "info",
        payload: {
          entityType,
          entityId,
          mock: true,
          success: true,
        },
      });

      this.robMutationCall.next(mockResponse);
    }, 1500);
  }

  searchInstitutions(searchTerm: string): Observable<FinancialInstitution[]> {
    const query = queries.getInstitutionSearchQuery(searchTerm);
    return this.httpService
      .doPostRequest<RobInstitutionSearchResponse>(UrlKey.rob_graphql_url, query)
      .pipe(
        map(
          (res) =>
            (res as RobInstitutionSearchResponse).data?.searchFinancialInstitutions
              ?.financialInstitutions ?? [],
        ),
      );
  }

  executeCombinedMutation(combinedMutation: any): void {
    this.httpService.doPostRequest(UrlKey.rob_graphql_url, combinedMutation).subscribe({
      next: (res: any) => {
        WebLogger.log({
          message: "ROB combined mutation succeeded",
          level: "info",
          payload: {
            event_type: "api_call_complete",
            service: "rob",
            api_name: "estate_profile",
            http_method: "POST",
            operation: "combined_mutation",
            status_code: 200,
          },
        });
        this.robMutationCall.next(res);
      },
      error: (err: unknown) => {
        WebLogger.log({
          message: "ROB combined mutation failed",
          level: "error",
          payload: {
            event_type: "api_call_failed",
            service: "rob",
            api_name: "estate_profile",
            http_method: "POST",
            operation: "combined_mutation",
            error_message: err instanceof Error ? err.message : String(err),
            error_type: err instanceof Error ? err.name : "UnknownError",
            status_code: 0,
          },
        });
        this.robMutationCall.error(err);
      },
    });
  }
}
